/**
 * หน้าผู้ปกครอง — โปรไฟล์ลูก · ตั้ง PIN · รหัสครอบครัว
 *
 * ⚠️ PDPA / CLAUDE.md ข้อ 8 — แสดงได้เฉพาะชื่อเล่น อวตาร ระดับชั้น
 *
 * ★ กฎเรื่อง PIN (ข้อควรระวังที่เจ้าของโปรเจกต์กำหนด):
 *   1. ต้องใส่ **สองครั้ง** เพื่อยืนยัน — พิมพ์ผิดครั้งเดียวแล้วลูกเข้าไม่ได้เลย
 *      และเราไม่มีทางกู้คืนได้เพราะเก็บเป็น argon2id hash
 *   2. **ห้ามแสดง PIN เป็นตัวเลขหลังตั้งเสร็จ** — แสดงได้แค่ตอนกำลังพิมพ์
 *      (ใช้ `type="password"` + `inputMode="numeric"`)
 *   3. ลืม PIN = **ตั้งใหม่ ไม่ใช่กู้ของเดิม** — ปุ่มจึงเขียนว่า "ตั้ง PIN ใหม่"
 *      ไม่ใช่ "ดู PIN"
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Center, Shell } from "@/components/Shell";
import { AvatarPicker } from "@/components/AvatarPicker";
import { AVATARS } from "@/lib/avatars";
import { GRADES } from "@/lib/grades";
import { translate as t } from "@/i18n";
import { ApiError, api } from "@/lib/api";
import { rememberOrgCode, useSession } from "@/lib/session";
import type { LearnerBrief } from "@/lib/types";

export function ParentPage(): JSX.Element {
  const navigate = useNavigate();
  const me = useSession((s) => s.me);
  const reload = useSession((s) => s.reload);
  const logout = useSession((s) => s.logout);
  const queryClient = useQueryClient();

  const [adding, setAdding] = useState(false);
  const [pinFor, setPinFor] = useState<LearnerBrief | null>(null);
  const [gradeFor, setGradeFor] = useState<LearnerBrief | null>(null);
  const [editingCode, setEditingCode] = useState(false);

  if (!me) {
    return (
      <Shell>
        <Center>
          <p>{t("state.loading")}</p>
        </Center>
      </Shell>
    );
  }

  const noKids = me.learners.length === 0;
  const noPins = me.learners.filter((k) => !k.has_pin);

  async function refresh(): Promise<void> {
    await reload();
    await queryClient.invalidateQueries();
  }

  return (
    <Shell here={t("parent.title")}>
      <h1>{me.org.name}</h1>
      <p className="sub">{me.user.display_name}</p>

      {/* ─── ขั้นถัดไปที่ต้องทำ — บอกให้ชัดว่าเหลืออะไร ─── */}
      {noKids ? (
        <div className="note">{t("parent.stepAddKid")}</div>
      ) : noPins.length > 0 ? (
        <div className="note">
          {t("parent.stepSetPin", "th", {
            names: noPins.map((k) => k.nickname).join(" · "),
          })}
        </div>
      ) : (
        <div className="note">
          {t("parent.stepReady", "th", { code: me.org.org_code })}
        </div>
      )}

      <p className="eyebrow">{t("parent.kids")}</p>
      <div className="topics">
        {me.learners.map((kid) => (
          <div key={kid.id} className="topic" style={{ cursor: "default" }}>
            <span className="face-sm">{AVATARS[kid.avatar] ?? "🙂"}</span>
            <span className="nm">{kid.nickname}</span>
            <span className="ct">{kid.grade_label}</span>
            {kid.has_pin ? null : (
              <span className="why">{t("parent.pinMissing")}</span>
            )}
            {/* ★ เด็กเลื่อนชั้นทุกปี — ต้องแก้ได้ตรงนี้ ประวัติเดิมไม่ถูกแตะ */}
            <button
              type="button"
              className="mini"
              onClick={() => setGradeFor(kid)}
            >
              {t("parent.changeGrade")}
            </button>
            <button
              type="button"
              className="mini"
              onClick={() => setPinFor(kid)}
            >
              {kid.has_pin ? t("parent.changePin") : t("parent.setPin")}
            </button>
          </div>
        ))}
        {noKids ? <div className="topic">{t("parent.noKids")}</div> : null}
      </div>

      <button type="button" className="go" onClick={() => setAdding(true)}>
        {t("parent.addKid")}
      </button>

      <p className="eyebrow">{t("parent.orgCode")}</p>
      <div
        className="note"
        style={{ display: "flex", alignItems: "center", gap: 12 }}
      >
        <div style={{ flex: 1 }}>
          <strong
            data-testid="org-code"
            style={{
              fontFamily: "var(--mono)",
              fontSize: 22,
              letterSpacing: ".06em",
            }}
          >
            {me.org.org_code}
          </strong>
          <br />
          {t("parent.orgCodeWhy")}
        </div>
        {/* D16 — ตั้งรหัสเองได้ ต้องยืนยันรหัสผ่านใน dialog */}
        <button
          type="button"
          className="mini"
          onClick={() => setEditingCode(true)}
        >
          {t("parent.changeOrgCode")}
        </button>
      </div>

      <div
        style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" }}
      >
        <button
          type="button"
          className="link"
          onClick={() => navigate("/learn")}
        >
          {t("parent.tryQuiz")}
        </button>
        <button type="button" className="link" onClick={() => void logout()}>
          {t("gate.signOut")}
        </button>
      </div>

      {adding ? (
        <AddKidDialog
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      ) : null}

      {gradeFor ? (
        <ChangeGradeDialog
          learner={gradeFor}
          onClose={() => setGradeFor(null)}
          onDone={async () => {
            setGradeFor(null);
            await refresh();
          }}
        />
      ) : null}

      {pinFor ? (
        <SetPinDialog
          learner={pinFor}
          onClose={() => setPinFor(null)}
          onDone={async () => {
            setPinFor(null);
            await refresh();
          }}
        />
      ) : null}

      {editingCode ? (
        <ChangeOrgCodeDialog
          current={me.org.org_code}
          onClose={() => setEditingCode(false)}
          onDone={async () => {
            setEditingCode(false);
            await refresh();
          }}
        />
      ) : null}
    </Shell>
  );
}

// ═══ รหัสครอบครัวตั้งเอง (D16) ══════════════════════════════════
const ORG_CODE_PREFIX = "TWP-";
const ORG_CODE_BODY = /^[A-Z0-9]{4,10}$/;

function ChangeOrgCodeDialog({
  current,
  onClose,
  onDone,
}: {
  current: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const [body, setBody] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const code = ORG_CODE_PREFIX + body;
  const canSubmit =
    ORG_CODE_BODY.test(body) && code !== current && password !== "" && !busy;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const org = await api.put<{ org_code: string }>("/auth/org-code", {
        org_code: code,
        parent_password: password,
      });
      // เครื่องนี้อาจเป็นเครื่องเดียวกับที่ลูกใช้ — จำรหัสใหม่ไว้เลย ไม่ให้เสนอรหัสตาย
      rememberOrgCode(org.org_code);
      await onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === "INVALID_CREDENTIALS") {
        setError(t("parent.wrongParentPassword"));
      } else if (e instanceof ApiError && e.code === "CONFLICT") {
        setError(t("parent.orgCodeTaken"));
      } else if (e instanceof ApiError && e.code === "VALIDATION_ERROR") {
        setError(t("parent.orgCodeInvalid"));
      } else {
        setError(e instanceof ApiError ? e.message : t("error.network"));
      }
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={t("parent.changeOrgCodeTitle")}
      subtitle={current}
      onClose={onClose}
    >
      {error ? <p className="err">{error}</p> : null}

      <div className="field">
        <label htmlFor="orgcode">{t("parent.newOrgCode")}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontFamily: "var(--mono)",
              fontSize: 18,
              color: "var(--ink-3)",
            }}
          >
            {ORG_CODE_PREFIX}
          </span>
          {/* พิมพ์เล็กกลายเป็นใหญ่ทันที · ตัวที่ไม่ใช่ A-Z/0-9 หายไปเลย ให้เห็นกติกาจากการพิมพ์ */}
          <input
            id="orgcode"
            value={body}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            style={{
              fontFamily: "var(--mono)",
              fontSize: 18,
              letterSpacing: ".06em",
              flex: 1,
            }}
            onChange={(e) =>
              setBody(
                e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "")
                  .slice(0, 10),
              )
            }
          />
        </div>
        <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
          {t("parent.orgCodeRule")}
        </span>
      </div>

      <div className="field">
        <label htmlFor="pw-code">{t("parent.confirmWithPassword")}</label>
        <input
          id="pw-code"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
        <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
          {t("parent.confirmWhy")}
        </span>
      </div>

      <button
        type="button"
        className="go"
        disabled={!canSubmit}
        onClick={() => void submit()}
      >
        {t("parent.saveOrgCode")}
      </button>
    </Dialog>
  );
}

// ═══ สร้างโปรไฟล์ลูก ════════════════════════════════════════════
function AddKidDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const [nickname, setNickname] = useState("");
  // ★ ไม่มีค่าเริ่มต้น — ผู้ปกครองต้องเลือกเอง ระบบเคยเดาว่าทุกคนเป็น ม.1 มาแล้ว
  const [grade, setGrade] = useState<string | null>(null);
  const [avatar, setAvatar] = useState("fox");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    if (!nickname.trim() || !grade || busy) return;
    setBusy(true);
    setError(null);
    try {
      // ⚠️ ไม่ส่ง pin มาพร้อมกัน — ตั้ง PIN เป็นขั้นตอนแยกที่ต้องยืนยันสองครั้ง
      await api.post("/learners", {
        nickname: nickname.trim(),
        grade,
        avatar,
      });
      await onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === "CONFLICT")
        setError(t("parent.nicknameTaken"));
      else setError(e instanceof ApiError ? e.message : t("error.network"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t("parent.addKid")} onClose={onClose}>
      {error ? <p className="err">{error}</p> : null}

      <div className="field">
        <label htmlFor="nickname">{t("parent.nickname")}</label>
        <input
          id="nickname"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          maxLength={40}
          autoComplete="off"
        />
        {/* PDPA — บอกตรง ๆ ว่าอย่าใส่ชื่อจริง */}
        <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
          {t("parent.nicknameWhy")}
        </span>
      </div>

      <div className="field">
        <label>{t("parent.grade")}</label>
        <GradePicker value={grade} onChange={setGrade} />
      </div>

      <div className="field">
        <label>{t("parent.avatar")}</label>
        <AvatarPicker value={avatar} onChange={setAvatar} />
      </div>

      <button
        type="button"
        className="go"
        disabled={!nickname.trim() || !grade || busy}
        onClick={() => void submit()}
      >
        {t("parent.addKidSubmit")}
      </button>
    </Dialog>
  );
}

// ═══ ระดับชั้น ══════════════════════════════════════════════════
function GradePicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (code: string) => void;
}): JSX.Element {
  // สองแถว ป./ม. — ป้ายมาจากตารางเดียวกับที่เซิร์ฟเวอร์รู้จัก
  return (
    <div className="jump" data-testid="grade-picker">
      {GRADES.map((g) => (
        <button
          key={g.code}
          type="button"
          className={value === g.code ? "now" : ""}
          aria-pressed={value === g.code}
          onClick={() => onChange(g.code)}
        >
          {g.label}
        </button>
      ))}
    </div>
  );
}

function ChangeGradeDialog({
  learner,
  onClose,
  onDone,
}: {
  learner: LearnerBrief;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const [grade, setGrade] = useState<string>(learner.grade);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    if (busy || grade === learner.grade) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/learners/${learner.id}`, { grade });
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("error.network"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={t("parent.changeGradeFor")}
      subtitle={learner.nickname}
      onClose={onClose}
    >
      {/* บอกตรง ๆ ว่าประวัติไม่หาย — ไม่งั้นผู้ปกครองจะกลัวไม่กล้าเลื่อนชั้น */}
      <p className="note">{t("parent.changeGradeWhy")}</p>
      {error ? <p className="err">{error}</p> : null}
      <div className="field">
        <label>{t("parent.grade")}</label>
        <GradePicker value={grade} onChange={setGrade} />
      </div>
      <button
        type="button"
        className="go"
        disabled={busy || grade === learner.grade}
        onClick={() => void submit()}
      >
        {t("parent.saveGrade")}
      </button>
    </Dialog>
  );
}

// ═══ ตั้ง / เปลี่ยน PIN ══════════════════════════════════════════
function SetPinDialog({
  learner,
  onClose,
  onDone,
}: {
  learner: LearnerBrief;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const digitsOk = /^\d{4}$/.test(pin);
  const matches = pin === confirm;
  const canSubmit = digitsOk && matches && password !== "" && !busy;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await api.put(`/learners/${learner.id}/pin`, {
        pin,
        parent_password: password,
      });
      await onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === "INVALID_CREDENTIALS") {
        setError(t("parent.wrongParentPassword"));
      } else if (e instanceof ApiError && e.code === "VALIDATION_ERROR") {
        setError(t("parent.weakPin"));
      } else {
        setError(e instanceof ApiError ? e.message : t("error.network"));
      }
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={learner.has_pin ? t("parent.changePinFor") : t("parent.setPinFor")}
      subtitle={learner.nickname}
      onClose={onClose}
    >
      {/* ลืม PIN = ตั้งใหม่ ไม่ใช่กู้ของเดิม — บอกไว้ตรงนี้ให้ชัด */}
      {learner.has_pin ? (
        <p className="note">{t("parent.pinCannotBeRecovered")}</p>
      ) : null}
      {error ? <p className="err">{error}</p> : null}

      <div className="field">
        <label htmlFor="pin">{t("parent.newPin")}</label>
        {/* ⚠️ type=password — ห้ามแสดง PIN เป็นตัวเลขบนหน้าจอ
            inputMode=numeric ทำให้ iPad ขึ้นแป้นตัวเลขแทนคีย์บอร์ดเต็ม */}
        <input
          id="pin"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={4}
          value={pin}
          onChange={(e) =>
            setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
          }
        />
      </div>

      <div className="field">
        <label htmlFor="pin2">{t("parent.confirmPin")}</label>
        <input
          id="pin2"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={4}
          value={confirm}
          onChange={(e) =>
            setConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))
          }
        />
        {confirm !== "" && !matches ? (
          <span style={{ fontSize: 13, color: "var(--bad)" }}>
            {t("parent.pinMismatch")}
          </span>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="pw">{t("parent.confirmWithPassword")}</label>
        <input
          id="pw"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
        <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
          {t("parent.confirmWhy")}
        </span>
      </div>

      <button
        type="button"
        className="go"
        disabled={!canSubmit}
        onClick={() => void submit()}
      >
        {t("parent.savePin")}
      </button>
    </Dialog>
  );
}

// ═══ กล่องซ้อน ══════════════════════════════════════════════════
function Dialog({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
      <div className="sheet-in">
        <div className="sheet-head">
          <div>
            <b>{title}</b>
            {subtitle ? <i>{subtitle}</i> : null}
          </div>
          <button type="button" className="quit" onClick={onClose}>
            {t("parent.cancel")}
          </button>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}
